/**
 * Слова для того, что API отдаёт кодом.
 *
 * Сервер не отдаёт готовый текст осознанно: иначе язык попал бы в ключ кэша
 * ответов и тайлов, и одна и та же карта хранилась бы дважды. Здесь коды
 * превращаются в подписи.
 *
 * Исключение — имена видов: они приходят обоими языками (`name`, `name_en`),
 * потому что это ДАННЫЕ, которые правит админка, а не подписи витрины.
 */
import { t } from './store'
import type { Lang } from './types'

/** Подпись слоя карты по его id (`sst` → «Температура воды»). */
export function layerLabel(id: string): string {
  return t(`layers.${id}`)
}

/** Единица измерения по коду. Пустой код — величины нет (фаза Луны). */
export function unitLabel(code: string | null | undefined): string {
  if (!code) return ''
  return t(`units.${code}`)
}

/** Имя вида на текущем языке. Английского нет → русское: вид без подписи хуже. */
export function speciesName(
  species: { name: string; name_en?: string | null },
  lang: Lang,
): string {
  // Латышских имён видов в базе нет, и пока не появятся — любой нерусский
  // кабинет берёт английское: кириллица в латышской витрине хуже английского
  // названия, которое в отрасли и так в ходу рядом с латынью.
  if (lang !== 'ru' && species.name_en) return species.name_en
  return species.name
}

/** Что пришло в `detail` ошибки кабинетного API. */
export interface ErrorDetail {
  code: string
  [param: string]: unknown
}

/** Ошибка от сервера в форме кода — или что-то другое (старый ответ, прокси). */
export function parseErrorDetail(detail: unknown): ErrorDetail | null {
  if (!detail || typeof detail !== 'object') return null
  const code = (detail as { code?: unknown }).code
  if (typeof code !== 'string' || !code) return null
  return detail as ErrorDetail
}

/**
 * Текст ошибки по её коду.
 *
 * `fallbackKey` — что сказать, если сервер ответил не кодом: 502 от прокси,
 * ответ старой версии бэкенда, обрыв сети. Молчать нельзя, а показывать
 * `[object Object]` — тем более.
 */
export function errorText(detail: unknown, fallbackKey = 'errors.unknown'): string {
  const parsed = parseErrorDetail(detail)
  if (!parsed) {
    // Строка от сервера, которую мы не превращали в код (публичный API, прокси):
    // показываем как есть — это лучше, чем стереть причину.
    return typeof detail === 'string' && detail.trim() ? detail : t(fallbackKey)
  }
  const vars: Record<string, string | number> = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (key === 'code') continue
    if (typeof value === 'string' || typeof value === 'number') vars[key] = value
  }
  return t(`errors.${parsed.code}`, vars)
}

/** Коды оговорок о статусе модели, для которых у витрины есть текст.
 *
 *  Список продублирован на сервере (`src/cabinet/species_catalog.py`) и там же
 *  отсекает чужие коды. Здесь он вторая линия: ответ мог прийти от бэкенда
 *  новее витрины, и показывать рыбаку сырой `foo_bar` нельзя. */
const CAVEAT_CODES = ['unvalidated_upstream'] as const

/** Текст оговорки по коду; null — оговорки нет или код витрине незнаком. */
export function speciesCaveat(code: string | null | undefined): string | null {
  if (!code) return null
  return (CAVEAT_CODES as readonly string[]).includes(code)
    ? t(`species.caveat.${code}`)
    : null
}
